import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    GaugeDivisionsDialogComponent,
    GaugeDivisionsDialogResult,
} from '@components/editor/gauge-divisions-dialog/gauge-divisions-dialog.component';
import { AutosavePage } from '@service/auto-save-controller.service';
import { GaugeExtensionService } from '@service/canvas/gauge-extension.service';
import { ElementAttribute } from '@src/app/models/editor';
import { Channel, ChannelFns } from 'icreate/common/models/channel';
import { lastValueFrom } from 'rxjs';
import { GaugeTitlePosition } from 'icreate/common/models/editor-viewer-element';
import { SystemColor } from 'icreate/common/models/system-color';
import { Vessel } from 'icreate/common/models/vessel';

@Component({
    selector: 'app-gauge-element-settings',
    templateUrl: './gauge-element-settings.component.html',
    styleUrl: './gauge-element-settings.component.css',
    standalone: false,
})
export class GaugeElementSettingsComponent implements OnChanges {
    @Input() element: SVGGElement;
    @Input() assignedChannel: Channel;
    @Input() systemColors: SystemColor[];
    @Input() vessel: Vessel;

    readonly TitlePositionOptions = [
        'mimic.settings.valve-object.interaction.position.bottom',
        'mimic.settings.valve-object.interaction.position.top',
    ];

    readonly AppearanceTypeOptions = ['mimic.library.default'];

    private readonly titlePositionMap: { [key: string]: GaugeTitlePosition } = {
        'mimic.settings.valve-object.interaction.position.bottom': GaugeTitlePosition.BOTTOM,
        'mimic.settings.valve-object.interaction.position.top': GaugeTitlePosition.TOP,
    };

    private readonly settingAttributes = [
        ElementAttribute.gaugeTitle,
        ElementAttribute.gaugeTitlePosition,
        ElementAttribute.gaugeUnitDisplay,
        ElementAttribute.gaugeValueDisplay,
        ElementAttribute.gaugeNameDisplay,
        ElementAttribute.gaugeDivisions,
        ElementAttribute.gaugeCustomDivisions,
        ElementAttribute.gaugeScaleValues,
    ];

    title: string = '';
    titlePositionKey: string = this.TitlePositionOptions[0];

    displayUnits: boolean = true;
    displayValue: boolean = true;
    displayName: boolean = true;

    divisionOptions: string[] = [];
    selectedDivision: string = '';
    isCustomDivisions: boolean = false;

    private initialElement: SVGGElement;

    constructor(
        private gaugeExtension: GaugeExtensionService,
        private translateService: TranslateService,
        private router: Router,
        private dialog: MatDialog
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.element) return;
        const setting = this.gaugeExtension.getSetting(this.element, this.assignedChannel);
        if (changes['element']) {
            this.initialElement = this.element.cloneNode(true) as SVGGElement;
            this.titlePositionKey = Object.keys(this.titlePositionMap).find(
                key => this.titlePositionMap[key] === setting.titlePosition
            );
            this.displayUnits = setting.unitDisplay;
            this.displayValue = setting.valueDisplay;
            this.displayName = setting.nameDisplay;
            this.divisionOptions = GaugeExtensionService.DIVISION_OPTIONS.map(divisions =>
                this.translateService.instant('mimic.settings.gauge.divisions.auto', { divisions })
            );
        }
        this.isCustomDivisions = setting.isCustom;
        this.selectedDivision = this.translateService.instant(
            setting.isCustom ? 'mimic.settings.gauge.divisions.custom' : 'mimic.settings.gauge.divisions.auto',
            { divisions: setting.divisions }
        );
        this.title = setting.title || this.assignedChannel?.opsData?.name || '';
    }

    handleTitleChange(event: { name: string; value: string }) {
        this.title = event.value;
        this.updateAttribute(ElementAttribute.gaugeTitle, this.title);
    }

    handleTitleBlur(event: { name: string; value: string }) {
        if (event.value) return;
        this.title = this.assignedChannel?.opsData?.name || '';
    }

    handleTitlePositionChange(event: { name: string; value: string }) {
        this.titlePositionKey = event.value;
        this.updateAttribute(ElementAttribute.gaugeTitlePosition, this.titlePositionMap[this.titlePositionKey]);
    }

    handleUnitDisplayChange(event) {
        this.displayUnits = event.checked;
        this.updateAttribute(ElementAttribute.gaugeUnitDisplay, String(this.displayUnits));
    }

    handleValueDisplayChange(event) {
        this.displayValue = event.checked;
        this.updateAttribute(ElementAttribute.gaugeValueDisplay, String(this.displayValue));
    }

    handleNameDisplayChange(event) {
        this.displayName = event.checked;
        this.updateAttribute(ElementAttribute.gaugeNameDisplay, String(this.displayName));
    }

    handleDivisionsChange(event: { name: string; value: string }) {
        const divisions = GaugeExtensionService.DIVISION_OPTIONS[this.divisionOptions.indexOf(event.value)];
        if (!divisions) return;
        this.selectedDivision = event.value;
        this.isCustomDivisions = false;
        this.element.setAttribute(ElementAttribute.gaugeCustomDivisions, 'false');
        this.element.removeAttribute(ElementAttribute.gaugeScaleValues);
        this.updateAttribute(ElementAttribute.gaugeDivisions, String(divisions));
    }

    async openCustomDivisionsDialog() {
        const setting = this.gaugeExtension.getSetting(this.element, this.assignedChannel);
        const measurableRange = this.assignedChannel.fcuData.measurableRange;
        const min = ChannelFns.scaleValue(this.assignedChannel, measurableRange.min);
        const max = ChannelFns.scaleValue(this.assignedChannel, measurableRange.max);
        const decimalPlaces = this.gaugeExtension.getDecimalPlaces(this.assignedChannel);
        const dialogRef = this.dialog.open(GaugeDivisionsDialogComponent, {
            data: {
                min,
                max,
                decimalPlaces,
                divisions: setting.divisions,
                scaleValues: setting.isCustom ? setting.scaleValues : null,
                autoSavePage: AutosavePage.MIMIC,
            },
        });
        const result: GaugeDivisionsDialogResult = await lastValueFrom(dialogRef.afterClosed());
        if (!result) return;
        this.isCustomDivisions = true;
        this.selectedDivision = this.translateService.instant('mimic.settings.gauge.divisions.custom', {
            divisions: result.divisions,
        });
        this.element.setAttribute(ElementAttribute.gaugeCustomDivisions, 'true');
        this.element.setAttribute(ElementAttribute.gaugeScaleValues, JSON.stringify(result.scaleValues));
        this.updateAttribute(ElementAttribute.gaugeDivisions, String(result.divisions));
    }

    openChannels() {
        const url = this.router.serializeUrl(
            this.router.createUrlTree([`channels/${this.vessel.shipyardId}/${this.vessel.id}`])
        );
        window.open(url, '_blank');
    }

    updateInitialElement(newElement) {
        this.initialElement = newElement.cloneNode(true);
    }

    get hasChanges(): boolean {
        if (!this.initialElement || !this.element) return false;
        return this.settingAttributes.some(
            attribute => this.initialElement.getAttribute(attribute) !== this.element.getAttribute(attribute)
        );
    }

    private updateAttribute(attribute: ElementAttribute, value: string) {
        this.element.setAttribute(attribute, value);
        this.gaugeExtension.render(this.element, this.assignedChannel, this.systemColors);
    }
}
